import React, { useState, useEffect, useCallback } from 'react';
import { tandianApi, auth, bloggerEnrollUrl } from '../../services/api';
import { AdminHeader, PrimaryBtn, GhostBtn, EmptyState } from './AdminLayout';
import QrModal from './QrModal';

// 把纯文本里的 http(s) 网址渲染成可点链接
function linkify(text) {
  if (!text) return text;
  return String(text).split(/(https?:\/\/[^\s]+)/g).map((p, i) =>
    /^https?:\/\//i.test(p)
      ? <a key={i} href={p} target="_blank" rel="noreferrer" style={{ color: '#FA1C51', textDecoration: 'underline', wordBreak: 'break-all' }} onClick={(e) => e.stopPropagation()}>{p}</a>
      : p
  );
}

const STATUS_TABS = [
  { key: '', label: '全部' },
  { key: 'pending', label: '待审核' },
  { key: 'approved', label: '已通过' },
  { key: 'rejected', label: '已拒绝' },
];
const STATUS_BADGE = {
  pending:  { text: '待审核', color: '#b45309', bg: '#fffbeb' },
  approved: { text: '已通过', color: '#15803d', bg: '#f0fdf4' },
  rejected: { text: '已拒绝', color: '#b91c1c', bg: '#fef2f2' },
};

export default function BloggerManage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [minFollowers, setMinFollowers] = useState('');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [openRows, setOpenRows] = useState(new Set());

  const toggleRow = (id) => setOpenRows((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allOpen = list.length > 0 && list.every((b) => openRows.has(b._id));
  const toggleAll = () => setOpenRows(allOpen ? new Set() : new Set(list.map((b) => b._id)));

  const load = useCallback(() => {
    setLoading(true);
    tandianApi.bloggers({
      status: status || undefined,
      q: q.trim() || undefined,
      minFollowers: minFollowers || undefined,
    }).then(setList).finally(() => setLoading(false));
  }, [status, q, minFollowers]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const review = async (id, action) => {
    await tandianApi.reviewBlogger(id, action);
    load();
  };

  const del = async (b) => {
    if (!window.confirm(`确认删除博主「${b.name || b.phone}」？删除后不可恢复。`)) return;
    await tandianApi.deleteBlogger(b._id);
    load();
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await tandianApi.updateBlogger(editing._id, {
        name: editing.name, xhsAccount: editing.xhsAccount, xhsUrl: editing.xhsUrl,
        xhsFollowers: Number(editing.xhsFollowers) || 0,
        cooperation: editing.cooperation, note: editing.note,
        platformBio: editing.platformBio || '',
        otherPlatforms: (editing.otherPlatforms || []).map((p) => ({ ...p, followers: Number(p.followers) || 0 })),
      });
      setEditing(null);
      load();
    } finally { setSaving(false); }
  };

  return (
    <div style={st.page}>
      <AdminHeader title="博主管理" back="/admin" actions={
        <div style={{ display: 'flex', gap: 8 }}>
          <PrimaryBtn onClick={() => setShowQr(true)}>邀请博主</PrimaryBtn>
          <GhostBtn small onClick={() => auth.logout()}>退出</GhostBtn>
        </div>
      } />

      <div style={st.body}>
        <div style={st.tabs}>
          {STATUS_TABS.map((t) => (
            <button key={t.key} style={{ ...st.tab, ...(status === t.key ? st.tabActive : {}) }} onClick={() => setStatus(t.key)}>{t.label}</button>
          ))}
        </div>
        <div style={st.filterRow}>
          <input style={st.search} placeholder="搜索姓名 / 小红书 / 手机号" value={q} onChange={(e) => setQ(e.target.value)} />
          <input style={st.minInput} type="tel" inputMode="numeric" placeholder="粉丝≥" value={minFollowers} onChange={(e) => setMinFollowers(e.target.value.replace(/\D/g, ''))} />
        </div>

        {loading ? (
          <div style={st.skeletons}>{[1, 2, 3].map((i) => <div key={i} style={st.skeleton} />)}</div>
        ) : list.length === 0 ? (
          <EmptyState text="暂无博主，点击右上角「邀请博主」生成入驻二维码" />
        ) : (<>
          <div style={st.listHead}>
            <button style={st.toggleAll} onClick={toggleAll}>{allOpen ? '收起全部' : '展开全部'}</button>
            <span style={st.count}>共 {list.length} 位</span>
          </div>
          <div style={st.list}>
            {list.map((b) => {
              const badge = STATUS_BADGE[b.status] || STATUS_BADGE.pending;
              const open = openRows.has(b._id);
              return (
                <div key={b._id} style={st.card}>
                  <div style={st.rowMain} onClick={() => toggleRow(b._id)}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={st.cardTop}>
                        <span style={st.name}>{b.name || '（未填姓名）'}</span>
                        {/* 两个标签包成一组靠右 —— 这一行是两端对齐，散着放第一个会被挤到中间 */}
                        <span style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                          <span style={{ ...st.badge, color: badge.color, background: badge.bg }}>{badge.text}</span>
                          {b.noDispatch && <span style={{ ...st.badge, color: '#b45309', background: '#fffbeb' }}>不派单</span>}
                        </span>
                      </div>
                      <div style={st.compactSub}>
                        {b.xhsAccount || '—'} · <b style={{ color: '#FA1C51' }}>{b.xhsFollowers || 0}粉</b> · {b.phone}
                        {/* 粉丝数是不是从入驻截图读出来的；不是本人「我」页面的截图要醒目（可能拿别人的主页冒充） */}
                        {b.xhsShot?.url && !b.xhsShot.followers
                          ? <span style={{ ...st.badge, marginLeft: 6, color: '#b45309', background: '#fffbeb' }}>截图没读出来，人工看一眼</span>
                          : b.xhsShot?.url
                          ? (b.xhsShot.isOwnPage
                            ? <span style={{ ...st.badge, marginLeft: 6, color: '#16a34a', background: '#f0fdf4' }}>截图核对过</span>
                            : <span style={{ ...st.badge, marginLeft: 6, color: '#b91c1c', background: '#fef2f2' }}>截图不是本人主页</span>)
                          : <span style={{ ...st.badge, marginLeft: 6, color: '#6b7280', background: '#f3f4f6' }}>粉丝数是自己填的</span>}
                      </div>
                    </div>
                    <button style={st.expandBtn}>{open ? '收起' : '展开'}</button>
                  </div>
                  {open && (
                    <div style={st.detail}>
                      <div style={st.sub}>手机 {b.phone}{b.cooperation ? ` · 配合度 ${b.cooperation}` : ''}</div>
                      {(b.otherPlatforms || []).length > 0 && (
                        <div style={st.platRow}>
                          {b.otherPlatforms.map((p, i) => (
                            <span key={i} style={st.platTag}>{p.type || '平台'} {p.account} · {p.followers || 0}粉</span>
                          ))}
                        </div>
                      )}
                      {b.xhsShot?.url && (
                        <div style={st.bioLine}>
                          <span style={st.bioTag}>主页截图</span>
                          {b.xhsShot.followers
                            ? <>读到：{b.xhsShot.nickname || '（没读到昵称）'}{b.xhsShot.redId ? ` · 小红书号 ${b.xhsShot.redId}` : ''} · {b.xhsShot.followers} 粉</>
                            : '系统没读出来，粉丝数是博主自己填的，请点开截图核对'}
                          {!b.xhsShot.followers || b.xhsShot.isOwnPage ? '' : ' · 这张不是本人登录的「我」页面（没有「编辑资料」），可能是拿别人的主页截的，审核时留意'}
                          {' '}<a style={st.inlineLink} href={b.xhsShot.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>看截图</a>
                        </div>
                      )}
                      {b.bio && <div style={st.bioLine}><span style={st.bioTag}>博主介绍</span>{linkify(b.bio)}</div>}
                      <div style={st.bioLine}><span style={st.bioTag}>平台介绍</span>{b.platformBio ? linkify(b.platformBio) : <span style={st.bioMuted}>（空，对外用博主介绍）</span>}</div>
                      {b.note && <div style={st.note}>{linkify(b.note)}</div>}
                      {b.xhsUrl && <div style={st.linkLine}>小红书主页：<a style={st.inlineLink} href={b.xhsUrl.match(/^https?:\/\//i) ? b.xhsUrl : `https://${b.xhsUrl}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>{b.xhsUrl}</a></div>}
                      <div style={st.actions}>
                        {b.status !== 'approved' && <button style={st.actOk} onClick={() => review(b._id, 'approve')}>通过</button>}
                        {/* 通过但不派单：系统不推荐、不邀请他，公开招募他自己能报名 */}
                        {b.status !== 'approved' && <button style={st.act} onClick={() => review(b._id, 'approve_nodispatch')}>通过，但不派单</button>}
                        {b.status === 'approved' && (b.noDispatch
                          ? <button style={st.act} onClick={() => review(b._id, 'dispatch_on')}>恢复派单</button>
                          : <button style={st.act} onClick={() => review(b._id, 'dispatch_off')}>不派单</button>)}
                        {/* 拒绝不通知博主（2026-10-01 会议） */}
                        {b.status !== 'rejected' && <button style={st.actNo} onClick={() => { if (window.confirm('拒绝？不会通知博主。')) review(b._id, 'reject'); }}>拒绝</button>}
                        <button style={st.act} onClick={() => setEditing({ ...b, xhsFollowers: b.xhsFollowers || '', otherPlatforms: b.otherPlatforms || [] })}>编辑</button>
                        {b.xhsUrl && <a style={st.act} href={b.xhsUrl} target="_blank" rel="noreferrer">主页</a>}
                        <button style={st.actDel} onClick={() => del(b)}>删除</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>)}
      </div>

      {editing && (
        <div style={st.overlay} onClick={() => setEditing(null)}>
          <div style={st.modal} onClick={(e) => e.stopPropagation()}>
            <p style={st.modalTitle}>编辑博主资料</p>
            <div style={st.modalScroll}>
              <EInput label="姓名" v={editing.name} on={(v) => setEditing((s) => ({ ...s, name: v }))} />
              <EInput label="小红书账号" v={editing.xhsAccount} on={(v) => setEditing((s) => ({ ...s, xhsAccount: v }))} />
              <EInput label="小红书主页链接" v={editing.xhsUrl} on={(v) => setEditing((s) => ({ ...s, xhsUrl: v }))} />
              <EInput label="小红书粉丝数" num v={editing.xhsFollowers} on={(v) => setEditing((s) => ({ ...s, xhsFollowers: v.replace(/\D/g, '') }))} />
              <EInput label="发帖配合度" v={editing.cooperation} on={(v) => setEditing((s) => ({ ...s, cooperation: v }))} />
              <div style={{ marginBottom: 10 }}>
                <div style={st.eLabel}>博主自己的介绍（只读）</div>
                <div style={st.bioReadonly}>{editing.bio || '（博主未填）'}</div>
              </div>
              <div style={{ marginBottom: 10 }}>
                <div style={st.eLabel}>平台介绍（对外复制用，留空＝用博主介绍）</div>
                <textarea style={st.eArea} value={editing.platformBio || ''} onChange={(e) => setEditing((s) => ({ ...s, platformBio: e.target.value }))} placeholder="留空则对外用博主自己的介绍" />
              </div>
              <EInput label="备注" v={editing.note} on={(v) => setEditing((s) => ({ ...s, note: v }))} />
            </div>
            <div style={st.modalBtns}>
              <GhostBtn onClick={() => setEditing(null)}>取消</GhostBtn>
              <PrimaryBtn onClick={saveEdit} disabled={saving}>{saving ? '保存中...' : '保存'}</PrimaryBtn>
            </div>
          </div>
        </div>
      )}

      {showQr && <QrModal url={bloggerEnrollUrl()} title="博主入驻二维码" onClose={() => setShowQr(false)} />}
    </div>
  );
}

function EInput({ label, v, on, num }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={st.eLabel}>{label}</div>
      <input style={st.eInput} value={v || ''} onChange={(e) => on(e.target.value)} inputMode={num ? 'numeric' : undefined} />
    </div>
  );
}

const st = {
  page: { minHeight: '100vh', background: '#f7f7f8', display: 'flex', flexDirection: 'column' },
  body: { flex: 1, padding: '12px 12px 24px' },
  tabs: { display: 'flex', gap: 6, marginBottom: 10 },
  tab: { flex: 1, padding: '8px 0', background: '#fff', color: '#6b7280', border: '1px solid #eee', borderRadius: 8, fontSize: 13, cursor: 'pointer' },
  tabActive: { background: '#FA1C51', color: '#fff', borderColor: '#FA1C51', fontWeight: 600 },
  filterRow: { display: 'flex', gap: 8, marginBottom: 12 },
  search: { flex: 1, padding: '10px 12px', border: '1px solid #eee', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' },
  minInput: { width: 90, padding: '10px 12px', border: '1px solid #eee', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' },
  skeletons: { display: 'flex', flexDirection: 'column', gap: 10 },
  skeleton: { height: 110, borderRadius: 12, background: '#efefef' },
  listHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  count: { fontSize: 13, color: '#6b7280', fontWeight: 600 },
  toggleAll: { padding: '5px 12px', background: '#fff7f8', color: '#FA1C51', border: '1px solid #ffd6de', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  list: { display: 'flex', flexDirection: 'column', gap: 6 },
  card: { background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' },
  rowMain: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', cursor: 'pointer' },
  compactSub: { fontSize: 12, color: '#9ca3af', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  expandBtn: { flexShrink: 0, padding: '5px 10px', background: 'none', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 12, color: '#6b7280', cursor: 'pointer' },
  detail: { padding: '8px 14px 12px', borderTop: '1px solid #f5f5f5' },
  linkLine: { fontSize: 12, color: '#6b7280', marginTop: 8, wordBreak: 'break-all' },
  inlineLink: { color: '#FA1C51', textDecoration: 'underline', wordBreak: 'break-all' },
  cardTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  name: { fontSize: 16, fontWeight: 700, color: '#111' },
  badge: { fontSize: 11, padding: '2px 8px', borderRadius: 5, fontWeight: 600 },
  metaRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 },
  xhs: { fontSize: 13, color: '#374151' },
  fans: { fontSize: 13, color: '#FA1C51', fontWeight: 700 },
  sub: { fontSize: 12, color: '#9ca3af', marginTop: 4 },
  platRow: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 },
  platTag: { fontSize: 11, color: '#374151', background: '#f3f4f6', padding: '2px 8px', borderRadius: 6 },
  note: { fontSize: 12, color: '#6b7280', marginTop: 8, background: '#fafafa', borderRadius: 6, padding: '6px 10px' },
  actions: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 },
  act: { padding: '6px 12px', background: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 7, cursor: 'pointer', fontSize: 13, textDecoration: 'none' },
  actOk: { padding: '6px 12px', background: '#FA1C51', color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  actNo: { padding: '6px 12px', background: '#fff', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  actDel: { padding: '6px 12px', background: '#fff', color: '#9ca3af', border: '1px solid #e5e7eb', borderRadius: 7, cursor: 'pointer', fontSize: 13 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  modal: { background: '#fff', borderRadius: 14, padding: 20, width: '100%', maxWidth: 360, maxHeight: '82vh', display: 'flex', flexDirection: 'column' },
  modalTitle: { fontSize: 15, fontWeight: 700, color: '#111', margin: '0 0 12px' },
  modalScroll: { overflowY: 'auto', flex: 1, marginBottom: 14 },
  eLabel: { fontSize: 12, color: '#6b7280', marginBottom: 5, fontWeight: 500 },
  eInput: { width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' },
  eArea: { width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', minHeight: 80, resize: 'vertical', lineHeight: 1.6 },
  bioReadonly: { fontSize: 13, color: '#6b7280', background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, padding: '8px 12px', lineHeight: 1.6, whiteSpace: 'pre-wrap' },
  bioLine: { fontSize: 13, color: '#374151', marginTop: 8, lineHeight: 1.6 },
  bioTag: { display: 'inline-block', fontSize: 11, color: '#7C3AED', background: '#f5f3ff', borderRadius: 5, padding: '1px 7px', marginRight: 6, fontWeight: 600 },
  bioMuted: { color: '#9ca3af' },
  modalBtns: { display: 'flex', gap: 8, justifyContent: 'flex-end' },
};
